import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, TextArea } from "../components/ui.tsx";
import { Modal } from "../components/dialog.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { useToast } from "../components/toast.tsx";
import { ApiError, post } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import type { ActionItem, InitiativeDetail, Unit } from "../lib/types.ts";
import { derivedDates, type Readiness } from "../../shared/planning.ts";
import { ReadonlyRows } from "../components/actions-readonly.tsx";
import { formatDateBR } from "../../shared/dates.ts";

interface Payload { title: string; description: string | null | undefined; unit_id: string | null; actions: ActionItem[] }

/** T07 review controls: confirm-and-submit for editors, reopen for instructors, link to the pending review. */
export function InitiativeActions({ detail, readiness, getPayload, onConflict }: {
  detail: InitiativeDetail;
  dirty: boolean;
  readiness: Readiness;
  getPayload: () => unknown;
  onConflict: (message: string) => void;
}) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState<{ payload: Payload; key: string } | null>(null);
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (d?: InitiativeDetail) => {
    if (d) qc.setQueryData(planKey(plan.id, "initiative", detail.id), d);
    await qc.invalidateQueries({ queryKey: planKey(plan.id) });
  };

  async function submit() {
    if (!confirming) return;
    setBusy(true);
    setError(null);
    try {
      // The same Idempotency-Key is reused if this dialog retries after a network failure.
      const res = await post<{ data: { initiative: InitiativeDetail; revision: { revision_number: number } } }>(
        `/plans/${plan.id}/initiatives/${detail.id}/submit`,
        { ...confirming.payload, expected_version: detail.version },
        { "Idempotency-Key": confirming.key },
      );
      setConfirming(null);
      await refresh(res.data.initiative);
      toast("success", `Versão submetida ${res.data.revision.revision_number} enviada ao instrutor.`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") {
        setConfirming(null);
        onConflict(e.message);
      } else setError(e instanceof Error ? e.message : "Não foi possível submeter.");
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    if (!reason.trim()) return setReasonError("Informe a justificativa da reabertura.");
    setBusy(true);
    try {
      const res = await post<{ data: InitiativeDetail }>(`/plans/${plan.id}/initiatives/${detail.id}/reopen`, { expected_version: detail.version, reason });
      setReopening(false);
      setReason("");
      await refresh(res.data);
      toast("success", "Iniciativa reaberta como rascunho.");
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") {
        setReopening(false);
        onConflict(e.message);
      } else toast("danger", e instanceof Error ? e.message : "Não foi possível reabrir.");
    } finally {
      setBusy(false);
    }
  }

  const p = confirming?.payload;
  const unitName = (id: string | null) => (qc.getQueryData<Unit[]>(planKey(plan.id, "units")) ?? []).find((u) => u.id === id)?.name ?? detail.unit?.name ?? "";
  const dates = p ? derivedDates(p.actions) : null;
  return (
    <>
      {detail.capabilities.can_submit && (
        <Button
          variant="secondary"
          disabled={!readiness.ready}
          onClick={() => setConfirming({ payload: getPayload() as Payload, key: crypto.randomUUID() })}
        >
          Revisar e submeter
        </Button>
      )}
      {detail.capabilities.can_submit && !readiness.ready && (
        // Disabled buttons don't show tooltips on touch/keyboard: say why next to it (UX-15).
        <a href="#pendencias" className="tap text-sm font-semibold text-warn-ink">
          {readiness.pending_fields.length === 1 ? "Falta 1 item para submeter" : `Faltam ${readiness.pending_fields.length} itens para submeter`}
        </a>
      )}
      {detail.capabilities.can_review && detail.current_revision && (
        <GuardedLink href={`/planos/${plan.id}/revisoes/${detail.current_revision.id}`} className="inline-flex rounded-ctl bg-primary px-4 py-2 text-sm font-semibold text-white">
          Revisar versão submetida
        </GuardedLink>
      )}
      {detail.capabilities.can_reopen && <Button variant="secondary" onClick={() => setReopening(true)}>Reabrir iniciativa</Button>}

      <Modal open={confirming !== null} onOpenChange={(o) => !o && !busy && setConfirming(null)} title="Submeter planejamento à revisão?" wide
        description="Confira os dados antes de enviar ao instrutor. Durante a revisão, esta iniciativa ficará bloqueada para edição."
        footer={<><Button variant="secondary" disabled={busy} onClick={() => setConfirming(null)}>Voltar e editar</Button><Button loading={busy} onClick={() => void submit()}>Confirmar submissão</Button></>}>
        {error && <Alert tone="danger">{error}</Alert>}
        {p && (
          <>
            <p className="font-semibold">{p.title}</p>
            <p className="text-sm text-muted">{unitName(p.unit_id)} · {p.actions.length} ações · {p.actions.reduce((s, a) => s + (a.weight ?? 0), 0)} pontos</p>
            {dates?.starts_on && <p className="text-sm text-muted">Período previsto: {formatDateBR(dates.starts_on)} a {formatDateBR(dates.ends_on)}</p>}
            <div className="mt-3"><ReadonlyRows rows={p.actions} /></div>
            <p className="mt-3 text-xs text-muted">O instrutor poderá validar o planejamento ou solicitar ajustes. Você poderá consultar a versão enviada e o histórico.</p>
          </>
        )}
      </Modal>

      <Modal open={reopening} onOpenChange={(o) => !o && !busy && setReopening(false)} title="Reabrir iniciativa?"
        description="A versão submetida e a decisão continuam no histórico. A iniciativa volta a rascunho e deixa de estar validada."
        footer={<><Button variant="secondary" disabled={busy} onClick={() => setReopening(false)}>Cancelar</Button><Button loading={busy} onClick={() => void reopen()}>Reabrir</Button></>}>
        <TextArea label="Justificativa" required rows={3} value={reason} error={reasonError} onChange={(e) => { setReason(e.target.value); setReasonError(undefined); }} />
      </Modal>
    </>
  );
}
